import {
    useCallback,
    useMemo,
    useState,
    useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
    Row,
    Col,
    Form,
    Input,
    DatePicker,
    Typography,
    Button,
} from 'antd';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import MUIFormItem from '@/components/MUIFormItem';
import DashedContainer from '@/components/DashedContainer';
import { CalendarIcon } from '@/assets/images/icons/screens';
import useApiValidationsForForm from '@/hooks/useApiValidationsForForm';
import useTranslatedValidationConfig from '@/hooks/useTranslatedValidationConfig';
import { dateFormat } from '@/helpers/dateFormat';
import { useCurrency } from '@/hooks/useCurrency';
import { actions } from '@/redux/accommodation/hotel/reducer';
import { selectors } from '@/redux/accommodation/hotel/create/model';

import Price from '../../Price';
import RoomPolicy from '../../RoomPolicy';
import baseValidations from './validations';

const formatRoomTypes = (items) => items.map((item) => (
    item.id
));

const formFields = [
    'name',
    'price',
    'start_date',
    'end_date',
    'room_types',
];

const AddNewSeason = ({ roomTypesList }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const { icon: currencyIcon } = useCurrency();

    const validations = useTranslatedValidationConfig(baseValidations);

    const [isRefundable, setIsRefundable] = useState(false);

    const options = useMemo(() => ([
        { label: t('%'), value: 4 },
        { label: t(currencyIcon), value: 5 },
    ]), [t]);

    const goToSeasonList = useCallback(() => {
        dispatch(actions.goToViewSeasonList());
    }, [dispatch]);

    const onFinish = useCallback((values) => {
        const data = {
            name: values.name,

            price_bb: values.price_bb,
            commission_bb: values.commission_bb,
            commission_type_bb: values.commission_type_bb,

            price_hb: values.price_hb,
            commission_hb: values.commission_hb,
            commission_type_hb: values.commission_type_hb,

            price_fb: values.price_fb,
            commission_fb: values.commission_fb,
            commission_type_fb: values.commission_type_fb,

            price_ai: values.price_ai,
            commission_ai: values.commission_ai,
            commission_type_ai: values.commission_type_ai,

            minimum_stay_required: values.minimum_stay_required,
            prepayment_amount: values.prepayment_amount,
            prepayment_amount_type: values.prepayment_amount_type,
            is_refundable: values.is_refundable,
            refundable_amount: values.refundable_amount,
            refundable_amount_type: values.refundable_amount_type,
            total_payment_required_before_check_in: values.total_payment_required_before_check_in,
            refundable_cancellation: values.refundable_cancellation,

            start_date: values.start_date?.format(dateFormat),
            end_date: values.end_date?.format(dateFormat),
            room_types: values.room_types,
        };
        dispatch(actions.requestSubmitAccommodationSeason(data));
    }, [dispatch]);

    const season = useSelector(selectors.seasonDetails);
    const errors = useSelector(selectors.errors);

    useApiValidationsForForm({ form, errors, formFields });

    const initialData = useMemo(() => {
        if (season) {
            return {
                name: season?.name,

                price_bb: season?.price_bb,
                commission_bb: season?.commission_bb,
                commission_type_bb: season?.commission_type_bb,

                price_hb: season?.price_hb,
                commission_hb: season?.commission_hb,
                commission_type_hb: season?.commission_type_hb,

                price_fb: season?.price_fb,
                commission_fb: season?.commission_fb,
                commission_type_fb: season?.commission_type_fb,

                price_ai: season?.price_ai,
                commission_ai: season?.commission_ai,
                commission_type_ai: season?.commission_type_ai,

                minimum_stay_required: season.minimum_stay_required,
                prepayment_amount: season.prepayment_amount,
                prepayment_amount_type: season.prepayment_amount_type,
                is_refundable: season.is_refundable,
                refundable_amount: season.refundable_amount,
                refundable_amount_type: season.refundable_amount_type,
                total_payment_required_before_check_in:
                    season.total_payment_required_before_check_in,
                refundable_cancellation: season.refundable_cancellation,

                start_date: moment(season?.start_date),
                end_date: moment(season?.end_date),
                room_types: season?.room_types ? formatRoomTypes(season?.room_types) : [],
            };
        } else {
            return {
                commission_type_bb: 4,
                commission_type_hb: 4,
                commission_type_fb: 4,
                commission_type_ai: 4,
                prepayment_amount_type: 4,
                refundable_amount_type: 4,
            };
        }
    }, [season]);

    useEffect(() => {
        if (season) {
            setIsRefundable(season.is_refundable);
        }
    }, [season]);

    return (
        <Form
            id="hotel-seasonal-price-form"
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onFinish}
            initialValues={initialData || {}}
        >
            <MUIFormItem
                label={t('Season Name')}
                name="name"
            >
                <Input />
            </MUIFormItem>

            <Row gutter={[28, 0]}>
                <Price
                    currency={currencyIcon}
                    options={options}
                    priceLabel="Price per Night BB"
                    priceName="price_bb"
                    commissionLabel="My Commission"
                    commissionName="commission_bb"
                    commissionTypeName="commission_type_bb"
                />
                <Price
                    currency={currencyIcon}
                    options={options}
                    priceLabel="Price per Night HB"
                    priceName="price_hb"
                    commissionLabel="My Commission"
                    commissionName="commission_hb"
                    commissionTypeName="commission_type_hb"
                />
                <Price
                    currency={currencyIcon}
                    options={options}
                    priceLabel="Price per Night FB"
                    priceName="price_fb"
                    commissionLabel="My Commission"
                    commissionName="commission_fb"
                    commissionTypeName="commission_type_fb"
                />
                <Price
                    currency={currencyIcon}
                    options={options}
                    priceLabel="Price per Night AI"
                    priceName="price_ai"
                    commissionLabel="My Commission"
                    commissionName="commission_ai"
                    commissionTypeName="commission_type_ai"
                />
            </Row>

            <Row gutter={0}>
                <Col xl={11} lg={24} md={12} sm={12} xs={24}>
                    <MUIFormItem
                        label={t('Start Season')}
                        name="start_date"
                    >
                        <DatePicker suffixIcon={<CalendarIcon />} />
                    </MUIFormItem>
                </Col>

                <Col xs={2}>
                    <div className="date-divider" />
                </Col>

                <Col xl={11} lg={24} md={12} sm={12} xs={24}>
                    <MUIFormItem
                        label={t('End Season')}
                        name="end_date"
                    >
                        <DatePicker suffixIcon={<CalendarIcon />} />
                    </MUIFormItem>
                </Col>
            </Row>

            <DashedContainer title="Season Policy">
                <Form.Item>
                    <RoomPolicy
                        currency={currencyIcon}
                        validations={validations}
                        isRefundable={isRefundable}
                        setIsRefundable={setIsRefundable}
                    />
                </Form.Item>
            </DashedContainer>

            <div className="checkbox-block">
                <Typography.Text>
                    {t('Select the Room Types you want to assign this Season')}
                </Typography.Text>
                <Form.Item name="room_types">
                    <CheckboxMultiSelect
                        options={roomTypesList}
                        labelParamName="type_name"
                        valueParamName="id"
                        selectAllLabel={t('Assign to all')}
                        initialCheckedList={initialData?.room_types || []}
                        col={12}
                    />
                </Form.Item>
            </div>

            <Form.Item>
                <Row justify="end">
                    <Col pull={1}>
                        <Button
                            danger
                            type="primary"
                            onClick={goToSeasonList}
                        >
                            {t('Cancel')}
                        </Button>
                    </Col>
                    <Col>
                        <Button type="primary" htmlType="submit">
                            {t('Submit')}
                        </Button>
                    </Col>
                </Row>
            </Form.Item>
        </Form>
    );
};

export default AddNewSeason;
